import React, { useState, useRef, useEffect } from "react";
import { Button, Form, Input, Card } from "antd";
import { toast } from "react-toastify";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import { EnvironmentOutlined, UserOutlined, PhoneOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { saveUserName } from "../../functions/user";

const libraries = ["places"];

const AddressForm = ({ handleClose }) => {
  const { user } = useSelector((state) => ({ ...state }));
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [isChanged, setIsChanged] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [addressValue, setAddressValue] = useState("");

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name || "",
        lastName: user.lastName || "",
        phoneNumber: user.PhoneNumber?.toString() || "",
        address: user.address || "",
        unitAddress: user.unitAddress || "",
        city: user.cityAddress || "",
        state: user.StateAddress || "",
        ZipAddress: user.ZipAddress || "",
        country: "India",
      });
      setAddressValue(user.address || "");
    }
  }, [user, form]);

  useEffect(() => {
    if (window.google) {
      setIsScriptLoaded(true);
    }
  }, []);

  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      const addressComponents = place.address_components;

      const getComponent = (type) => {
        const component = addressComponents.find((comp) =>
          comp.types.includes(type)
        );
        return component ? component.long_name : "";
      };

      const formattedAddress = place.formatted_address;
      const city = getComponent("locality");
      const state = getComponent("administrative_area_level_1");
      const zipCode = getComponent("postal_code");
      const country = getComponent("country");

      form.setFieldsValue({
        address: formattedAddress,
        city,
        state,
        ZipAddress: zipCode,
        country,
      });

      setAddressValue(formattedAddress);
      setIsChanged(true);
    } else {
      console.error("Autocomplete is not loaded yet!");
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        PhoneNumber: Number(values.phoneNumber),
        ZipAddress: values.ZipAddress || "",
      };

      const res = await saveUserName(user.token, payload);

      if (res.data.ok) {
        toast.success("Address saved successfully!");
        const updatedUser = { ...user, ...payload };
        dispatch({
          type: "LOGGED_IN_USER",
          payload: updatedUser,
        });
        handleClose();
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Failed to save address. Please try again.");
    }
  };

  return (
    <Card title="Update Address" bordered style={{ maxWidth: 600, margin: "20px auto" }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        onFieldsChange={() => setIsChanged(true)}
      >
        <Form.Item label="First Name" name="name" rules={[{ required: true, message: "Please enter your first name" }]}>
          <Input prefix={<UserOutlined />} placeholder="First Name" />
        </Form.Item>

        <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: "Please enter your last name" }]}>
          <Input prefix={<UserOutlined />} placeholder="Last Name" />
        </Form.Item>

        <Form.Item label="Phone Number" name="phoneNumber" rules={[
          { required: true, message: "Please enter your phone number" },
          { pattern: /^[0-9]{10}$/, message: "Enter a valid 10-digit number" },
        ]}>
          <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
        </Form.Item>

        <Form.Item label="Address" name="address" rules={[{ required: true, message: "Please enter your address" }]}>
          <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
            <Input
              ref={inputRef}
              prefix={<EnvironmentOutlined />}
              placeholder="Search your address"
              value={addressValue}
              onChange={(e) => setAddressValue(e.target.value)}
            />
          </Autocomplete>
        </Form.Item>

        <Form.Item label="Zip Code" name="ZipAddress" rules={[{ required: true, message: "Please enter your zip code" }, { pattern: /^[0-9]{5,6}$/, message: "Enter a valid zip code" }]}>
          <Input placeholder="Zip Code" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" block disabled={!isChanged}>
            Save Address
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default AddressForm;
